/**
 * Plan Features Utility
 * Defines feature access and limits for Starter vs Premium plans
 */

export const PLANS = {
    TRIAL: 'trial',
    STARTER: 'starter',
    PREMIUM: 'premium'
};

export const PLAN_LIMITS = {
    [PLANS.STARTER]: {
        menuItems: 120,
        tables: 20,
        branches: 1,
        languages: ['en', 'fr', 'te', 'es'], // English, French, Telugu, Spanish
        features: {
            // Core Features
            menu: true,
            tables: true,
            orders: true,
            kitchen: true,
            payments: true,
            customerPage: true,
            selfService: true,

            // Analytics
            basicAnalytics: true,
            advancedAnalytics: false,
            downloadReports: false,

            // Inventory & Ingredients
            ingredients: false,
            lowStockAlerts: false,
            costManagement: false,

            // Staff & HR
            staffManagement: true,
            attendance: true,
            leaveManagement: false,
            salaryReports: false,

            // Advanced Features
            aiNutrition: false,
            bulkUpload: false,
            multiBranch: false,
            customRoles: false,
            rewardsSystem: false,
            manualOrdering: false,

            // Support
            prioritySupport: false
        }
    },
    [PLANS.PREMIUM]: {
        menuItems: Infinity,
        tables: Infinity,
        branches: Infinity,
        languages: ['en', 'te', 'hi', 'zh', 'fr', 'ta', 'ml', 'es'], // All 8
        features: {
            // All features enabled
            menu: true,
            tables: true,
            orders: true,
            kitchen: true,
            payments: true,
            customerPage: true,
            selfService: true,

            // Analytics
            basicAnalytics: true,
            advancedAnalytics: true,
            downloadReports: true,

            // Inventory & Ingredients
            ingredients: true,
            lowStockAlerts: true,
            costManagement: true,

            // Staff & HR
            staffManagement: true,
            attendance: true,
            leaveManagement: true,
            salaryReports: true,

            // Advanced Features
            aiNutrition: true,
            bulkUpload: true,
            multiBranch: true,
            customRoles: true,
            rewardsSystem: true,
            manualOrdering: true,

            // Support
            prioritySupport: true
        }
    },
    [PLANS.TRIAL]: {
        // Trial gets all Premium features
        menuItems: Infinity,
        tables: Infinity,
        branches: Infinity,
        languages: ['en', 'te', 'hi', 'zh', 'fr', 'ta', 'ml', 'es'],
        features: {
            menu: true,
            tables: true,
            orders: true,
            kitchen: true,
            payments: true,
            customerPage: true,
            selfService: true,
            basicAnalytics: true,
            advancedAnalytics: true,
            downloadReports: true,
            ingredients: true,
            lowStockAlerts: true,
            costManagement: true,
            staffManagement: true,
            attendance: true,
            leaveManagement: true,
            salaryReports: true,
            aiNutrition: true,
            bulkUpload: true,
            multiBranch: true,
            customRoles: true,
            prioritySupport: true,
            rewardsSystem: true,
            manualOrdering: true
        }
    }
};

/**
 * Get current plan based on company profile
 */
export const getCurrentPlan = (companyProfile) => {
    if (!companyProfile) return PLANS.TRIAL;

    // If trial not expired and not paid, user is in trial
    if (companyProfile.trial_ends_at && !companyProfile.has_paid) {
        const trialEnd = new Date(companyProfile.trial_ends_at);
        const now = new Date();
        if (trialEnd > now) {
            return PLANS.TRIAL;
        }
    }

    // If has paid, return their subscription plan
    if (companyProfile.has_paid) {
        const plan = companyProfile.subscription_plan?.toLowerCase();
        return plan === 'premium' || plan === 'pro' ? PLANS.PREMIUM : PLANS.STARTER;
    }

    // Default to trial (shouldn't happen but safe default)
    return PLANS.TRIAL;
};

/**
 * Check if a feature is available for the current plan
 */
export const hasFeature = (companyProfile, featureName) => {
    const currentPlan = getCurrentPlan(companyProfile);
    const limits = PLAN_LIMITS[currentPlan];
    return limits?.features?.[featureName] || false;
};

/**
 * Get limit value for the current plan
 */
export const getLimit = (companyProfile, limitName) => {
    const currentPlan = getCurrentPlan(companyProfile);
    const limits = PLAN_LIMITS[currentPlan];
    return limits?.[limitName];
};

/**
 * Check if user has reached a limit
 */
export const hasReachedLimit = (companyProfile, limitName, currentCount) => {
    const limit = getLimit(companyProfile, limitName);
    if (limit === Infinity) return false;
    return currentCount >= limit;
};

/**
 * Check if language is supported in current plan
 */
export const isLanguageSupported = (companyProfile, languageCode) => {
    const currentPlan = getCurrentPlan(companyProfile);
    const limits = PLAN_LIMITS[currentPlan];
    return limits?.languages?.includes(languageCode) || false;
};

/**
 * Get upgrade message for a feature
 */
export const getUpgradeMessage = (featureName) => {
    const messages = {
        ingredients: 'Ingredients & Inventory Management is a Premium feature',
        attendance: 'Staff Attendance Tracking is a Premium feature',
        leaveManagement: 'Leave Management is a Premium feature',
        salaryReports: 'Salary Reports are a Premium feature',
        aiNutrition: 'AI Nutritional Info Generation is a Premium feature',
        bulkUpload: 'Bulk Upload/Download is a Premium feature',
        multiBranch: 'Multi-Branch Management is a Premium feature',
        customRoles: 'Custom Roles & Permissions are a Premium feature',
        advancedAnalytics: 'Advanced Analytics is a Premium feature',
        downloadReports: 'Report Downloads (Excel/PDF) are a Premium feature',
    };

    return messages[featureName] || 'This feature is available in the Premium plan';
};
